<script setup>
import HazardIcon from '@/Components/HazardIcon.vue';
// A trail's own page on the website (the `trail_detail` block, which
// only PublicSiteController::dispatchTrail places). Laid out from the
// design's Trail detail screen; the map is the /t page's drawing —
// grid, start and finish, compass, scale bar — in the design's
// colors, with its elevation strip taken off and an elevation chart of
// its own under it. One component for both templates: everything it
// draws reads --accent / --tint / --r, which both of them set.
//
// Every number arrives from the server (App\Support\Website\SiteTrail)
// as the rest of the product computes it: distance and gain are the
// stored stats, the chart is ElevationProfile. Nothing here measures a
// trail of its own, so this page cannot disagree with the hub about
// one.
import { computed, inject, onBeforeUnmount, onMounted, ref, unref } from 'vue';
import { hazardDetails, publicHazardTone } from '@/lib/publicHazard.js';
import { KM_TO_MILES, M_TO_FEET } from '@/lib/units.js';
import { DIFFICULTY_KEYS, difficultyLabel as difficultyWords, directionLabel } from '@/lib/trailLabels.js';
import DifficultyIcon from '@/Components/DifficultyIcon.vue';
import { iconSetForUse } from '@/lib/trailIconSet.js';
import { parseCalendarDate } from '@/lib/calendarDate.js';
import { contourDrawing } from '@/lib/trailContours.js';
import { trailFeature, trailType } from '@/lib/trailCatalog.js';
import { poiGlyph } from '@/lib/poiGlyphs.js';
import { SHORT_BELOW } from './siteLength.js';

const props = defineProps({
    config: { type: Object, default: () => ({}) },
    // One part of the page, when the page is built from the trail
    // template's blocks (trail_header, trail_map, …): 'header', 'map',
    // 'details', 'about', 'weather', 'hazards' or 'nearby'. Empty draws
    // the whole page, which is what a site with no template row gets.
    part: { type: String, default: '' },
    org: { type: Object, default: () => ({}) },
    site_data: { type: Object, default: () => ({}) },
    accent: { type: String, default: '' },
});
const on = (p) => !props.part || props.part === p;
// A block's own heading wins over the page's default words.
const heading = (fallback, key = 'title') => (props.part && String(props.config?.[key] || '').trim()) || fallback;

const trail = inject('tiqCurrentTrail', null);
// A plain object on the site; a ref in the builder, which swaps the
// sample trail when the manager switches pages.
const t = computed(() => unref(trail) || null);
const imperial = computed(() => t.value?.units === 'imperial');

// ── Words ────────────────────────────────────────────────────────
const STATUS = {
    open: { tone: 'open', label: 'Open' },
    caution: { tone: 'caution', label: 'Caution' },
    closed: { tone: 'closed', label: 'Closed' },
};
function bucket(condition) {
    if (['open', 'good', 'excellent'].includes(condition)) return 'open';
    if (['closed', 'unsafe'].includes(condition)) return 'closed';
    return 'caution';
}
const status = computed(() => (t.value?.show_status && t.value?.condition ? STATUS[bucket(t.value.condition)] : null));
const conditionWords = computed(() => {
    const c = String(t.value?.condition || '').replace(/_/g, ' ');
    return c ? c.charAt(0).toUpperCase() + c.slice(1) : '';
});
const difficultyLabel = computed(() => difficultyWords(t.value?.difficulty) || null);
// The trail's difficulty symbol, in the same color its line is drawn
// in (SiteTrail::lineColor reads the organization's own palette), so
// the page and the Trails list show one trail the same way.
const difficultyIcon = computed(() => (DIFFICULTY_KEYS.includes(t.value?.difficulty)
    ? { difficulty: t.value.difficulty, set: iconSetForUse(t.value.primary_use), color: t.value.color || null }
    : null));
// A hazard box is two parts, the report and when it was made: the
// right-hand part reads "Reported" over the day.
const reportedDay = (ymd) => parseCalendarDate(ymd)?.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) || '';

// ── Trails nearby ────────────────────────────────────────────────
// The org's other public trails, closest first (SiteTrail::nearby).
// The dot is the trail's status, as on the Trails list; with public
// status off there is none, and the difficulty symbol stands in.
const NEARBY_DOTS = { open: '#2e9b4f', caution: '#d99a1e', closed: '#d2452f' };
const nearby = computed(() => (t.value?.nearby || []).map((n) => {
    const tone = n.condition ? bucket(n.condition) : null;
    const away = n.connects ? 'Connects' : n.distance ? `${n.distance.value} ${n.distance.unit} away` : null;
    return {
        slug: n.slug,
        name: n.name,
        // The /t page sends its own (/t/{org}/{slug}); the website's
        // trail pages live at /trails/{slug}.
        href: n.href || `/trails/${n.slug}`,
        dot: tone ? NEARBY_DOTS[tone] : null,
        statusLabel: tone ? STATUS[tone].label : '',
        icon: DIFFICULTY_KEYS.includes(n.difficulty) ? { difficulty: n.difficulty, set: iconSetForUse(n.primary_use), color: n.color || null } : null,
        meta: [away, difficultyWords(n.difficulty), n.use_label].filter(Boolean).join(' · '),
    };
}));
const metaRest = computed(() => [t.value?.uses?.[0], t.value?.network].filter(Boolean).join(' · '));
const metaLine = computed(() => [difficultyLabel.value, metaRest.value].filter(Boolean).join(' · '));

const lenUnit = computed(() => (imperial.value ? 'mi' : 'km'));
const toLen = (m) => (imperial.value ? (m / 1000) * KM_TO_MILES : m / 1000);
const toHeight = (m) => (imperial.value ? m * M_TO_FEET : m);
const hUnit = computed(() => (imperial.value ? 'ft' : 'm'));
const fmtInt = (n) => Math.round(n).toLocaleString('en-US');
// A trail shorter than a twentieth of a mile (or km) prints as "0.0",
// which says nothing. Below that the page talks in feet (or meters)
// instead, everywhere it states a length along this trail, so the
// stat, the profile's axis and its readout agree with each other.
const shortTrail = computed(() => totalM.value > 0 && toLen(totalM.value) < SHORT_BELOW);
const shortUnit = computed(() => (imperial.value ? 'ft' : 'm'));
const fmtLen = (m) => (shortTrail.value
    ? { v: fmtInt(toHeight(m)), u: shortUnit.value }
    : { v: toLen(m).toFixed(1), u: lenUnit.value });

const updatedLabel = computed(() => {
    if (!t.value?.updated_at) return '';
    const d = new Date(t.value.updated_at);
    if (Number.isNaN(d.getTime())) return '';
    return 'Updated ' + d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
});

// Back to wherever the visitor came from on this site, or its Trails
// page, which every site has. The /t page names its own (the hub).
const backHref = ref(unref(trail)?.back_href || '/trails');
onMounted(() => {
    try {
        const from = document.referrer ? new URL(document.referrer) : null;
        if (from && from.origin === window.location.origin && from.pathname !== window.location.pathname) {
            backHref.value = from.pathname + from.search + from.hash;
        }
    } catch { /* keep home */ }
});

// ── Along the line ───────────────────────────────────────────────
// Metres along the drawn line at each vertex, so a hazard and a point
// on the chart can be placed on the map. Scaled to the profile's own
// total so the two agree about where "halfway" is.
const coords = computed(() => (t.value?.coordinates || []).filter((c) => Array.isArray(c) && c.length >= 2));
const cum = computed(() => {
    const cs = coords.value;
    const out = [0];
    for (let i = 1; i < cs.length; i++) {
        const [lng1, lat1] = cs[i - 1], [lng2, lat2] = cs[i];
        const dx = (lng2 - lng1) * Math.cos(((lat1 + lat2) / 2) * Math.PI / 180) * 111320;
        const dy = (lat2 - lat1) * 110540;
        out.push(out[i - 1] + Math.hypot(dx, dy));
    }
    return out;
});
const lineTotal = computed(() => cum.value[cum.value.length - 1] || 0);
const profile = computed(() => (t.value?.profile?.points?.length >= 2 ? t.value.profile : null));
const totalM = computed(() => profile.value?.total_m || t.value?.distance_m || lineTotal.value || 0);

/** Fraction 0..1 along the line → [lng, lat]. */
function lngLatAt(frac) {
    const cs = coords.value;
    if (!cs.length) return null;
    const target = Math.max(0, Math.min(1, frac)) * lineTotal.value;
    const c = cum.value;
    let i = 1;
    while (i < c.length - 1 && c[i] < target) i++;
    const span = c[i] - c[i - 1] || 1;
    const k = Math.max(0, Math.min(1, (target - c[i - 1]) / span));
    const a = cs[i - 1], b = cs[i] || a;
    return [a[0] + (b[0] - a[0]) * k, a[1] + (b[1] - a[1]) * k];
}

/** [lng, lat] → fraction along the line of its nearest vertex. */
function fracNearest(lng, lat) {
    const cs = coords.value;
    if (!cs.length || !lineTotal.value) return 0;
    const cosLat = Math.cos(lat * Math.PI / 180);
    let best = 0, bestD = Infinity;
    for (let i = 0; i < cs.length; i++) {
        const dx = (cs[i][0] - lng) * cosLat, dy = cs[i][1] - lat;
        const d = dx * dx + dy * dy;
        if (d < bestD) { bestD = d; best = i; }
    }
    return cum.value[best] / lineTotal.value;
}

// The points of interest tied to this trail (SiteTrail::pois): its
// trailhead, a viewpoint, a water source. The glyph is the one the
// type carries in the dashboard, from the light built-in set.
const pois = computed(() => (t.value?.pois || [])
    .filter((q) => Number.isFinite(Number(q.lat)) && Number.isFinite(Number(q.lng)))
    .map((q) => ({ ...q, lat: Number(q.lat), lng: Number(q.lng), tipKey: `poi-${q.id}`, glyph: poiGlyph(q.icon, q.category) })));

// ── The map: the /t page's drawing, in the design's colors ──────
const W = 1000, H = 500, PAD = 56;
const drawing = computed(() => {
    const cs = coords.value;
    if (cs.length < 2) return null;
    let minLng = Infinity, maxLng = -Infinity, minLat = Infinity, maxLat = -Infinity;
    for (const [lng, lat] of cs) {
        if (lng < minLng) minLng = lng;
        if (lng > maxLng) maxLng = lng;
        if (lat < minLat) minLat = lat;
        if (lat > maxLat) maxLat = lat;
    }
    const cosLat = Math.cos(((minLat + maxLat) / 2) * Math.PI / 180) || 1;
    const dLng = (maxLng - minLng) * cosLat || 1e-6;
    const dLat = (maxLat - minLat) || 1e-6;
    const s = Math.min((W - PAD * 2) / dLng, (H - PAD * 2) / dLat);
    const offX = PAD + ((W - PAD * 2) - dLng * s) / 2;
    const offY = PAD + ((H - PAD * 2) - dLat * s) / 2;
    const project = (lng, lat) => ({ x: offX + (lng - minLng) * cosLat * s, y: offY + (dLat - (lat - minLat)) * s });

    const pts = cs.map(([lng, lat]) => project(lng, lat));
    const route = pts.map((p, i) => `${i ? 'L' : 'M'}${p.x.toFixed(1)} ${p.y.toFixed(1)}`).join(' ');

    const grid = [];
    for (let i = 1; i < 8; i++) grid.push(`M${(W / 8) * i} 0V${H}`);
    for (let i = 1; i < 4; i++) grid.push(`M0 ${(H / 4) * i}H${W}`);

    // Scale bar: the largest round distance under ~160 units.
    const metresPerUnit = 111320 / s;
    const unitM = imperial.value ? 1609.344 : 1000;
    const nice = (v) => {
        if (v <= 0) return 1;
        const base = Math.pow(10, Math.floor(Math.log10(v)));
        const m = v / base;
        return (m >= 5 ? 5 : m >= 2 ? 2 : 1) * base;
    };
    let scaleLabel, scaleM;
    const targetM = 160 * metresPerUnit;
    if (imperial.value) {
        if (targetM >= 0.1 * unitM) { const mi = nice(targetM / unitM); scaleM = mi * unitM; scaleLabel = `${mi} mi`; }
        else { const ft = nice(targetM * M_TO_FEET); scaleM = ft / M_TO_FEET; scaleLabel = `${ft} ft`; }
    } else if (targetM >= 1000) { const km = nice(targetM / 1000); scaleM = km * 1000; scaleLabel = `${km} km`; }
    else { scaleM = nice(targetM); scaleLabel = `${scaleM} m`; }

    const hazards = [];
    for (const h of [...(t.value?.hazards || [])].reverse()) {
        const lat = Number(h.lat), lng = Number(h.lng);
        if (h.lat == null || h.lng == null || !Number.isFinite(lat) || !Number.isFinite(lng)) continue;
        const p = project(lng, lat);
        hazards.push({ id: h.key || h.id, x: Math.min(W - 20, Math.max(20, p.x)), y: Math.min(H - 14, Math.max(24, p.y)), fill: hazardTone(h.level).bg, label: `${h.level_label}: ${h.title}` });
    }

    // The trail's points of interest, as pins whose point is the spot.
    // Drawn under the hazards: a hazard is the one marker a visitor
    // must not miss.
    const poiMarks = [];
    for (const q of pois.value) {
        const p = project(q.lng, q.lat);
        poiMarks.push({ id: q.tipKey, x: Math.min(W - 20, Math.max(20, p.x)), y: Math.min(H - 4, Math.max(48, p.y)), glyph: q.glyph, label: `${q.type}: ${q.name}` });
    }

    // Real contour lines from the DEM baked around the trail
    // (TrailContours on the server); the grid only draws without them.
    const contours = contourDrawing(t.value?.contours, project, {
        labelMinLen: 160, labelGap: 140, maxLabels: 7,
        bounds: { x0: 40, y0: 40, x1: W - 100, y1: H - 70 },
    });

    return {
        route,
        grid,
        contours,
        start: pts[0],
        end: pts[pts.length - 1],
        hazards,
        pois: poiMarks,
        project,
        scale: { width: scaleM / metresPerUnit, label: scaleLabel },
    };
});

// ── The elevation chart ──────────────────────────────────────────
const CH = 200;
const chart = computed(() => {
    const p = profile.value;
    if (!p) return null;
    const pts = p.points;
    const lo = Math.min(...pts.map((q) => q.e)), hi = Math.max(...pts.map((q) => q.e));
    const span = hi - lo || 1;
    const total = pts[pts.length - 1].d || 1;
    const x = (d) => (d / total) * 1000;
    const y = (e) => 12 + (1 - (e - lo) / span) * (CH - 24);
    const line = pts.map((q, i) => `${i ? 'L' : 'M'}${x(q.d).toFixed(1)} ${y(q.e).toFixed(1)}`).join(' ');

    const short = shortTrail.value;
    const lenTotal = short ? toHeight(total) : toLen(total);
    const unitLabel = (short ? shortUnit.value : lenUnit.value).toUpperCase();
    const step = short
        ? [5, 10, 25, 50, 100, 250].find((s) => lenTotal / s <= 6) || 500
        : lenTotal <= 1.5 ? 0.25 : lenTotal <= 3 ? 0.5 : lenTotal <= 6 ? 1 : lenTotal <= 15 ? 2 : lenTotal <= 40 ? 5 : 10;
    const ticks = [];
    // A tick too close to the end would sit on the edge of the chart.
    for (let k = 0; k * step <= lenTotal * 0.97; k++) {
        const v = k * step;
        ticks.push({ pct: (v / lenTotal) * 100, label: v === 0 ? `0 ${unitLabel}` : String(Number(v.toFixed(2))) });
    }

    return {
        line,
        area: `${line} L1000 ${CH} L0 ${CH} Z`,
        maxLabel: `${fmtInt(toHeight(hi))} ${hUnit.value.toUpperCase()}`,
        minLabel: `${fmtInt(toHeight(lo))} ${hUnit.value.toUpperCase()}`,
        ticks,
        y,
        total,
    };
});

// The design's three hazard tones, which are its status colors: the
// two most severe levels read as closed-red, caution as amber, a
// notice as slate. `bg` is the sign, `soft` the box, `ink` the words.
const DESIGN_HAZARD_TONES = {
    danger: { bg: '#d2452f', fg: '#ffffff', soft: '#f8e2df', ink: '#9b2a1f' },
    warning: { bg: '#d2452f', fg: '#ffffff', soft: '#f8e2df', ink: '#9b2a1f' },
    caution: { bg: '#d99a1e', fg: '#ffffff', soft: '#fbf0d9', ink: '#7d5200' },
    notice: { bg: '#6b7f8f', fg: '#ffffff', soft: '#e8ecef', ink: '#3b4a57' },
};
function hazardTone(level) { return DESIGN_HAZARD_TONES[level] || publicHazardTone(level); }

// Published hazards, placed along the trail by the vertex nearest them.
const hazards = computed(() => (t.value?.hazards || []).map((h) => {
    const tone = hazardTone(h.level);
    // `key` is unique across kinds (an item and a structure can share
    // an id); the id alone opened the wrong card when they did.
    const placed = h.lat != null && h.lng != null && coords.value.length >= 2;
    const frac = placed ? fracNearest(Number(h.lng), Number(h.lat)) : null;
    return {
        ...h,
        tipKey: h.key || h.id,
        tone,
        frac,
        along: frac != null
            ? (shortTrail.value
                ? `${fmtInt(toHeight(frac * totalM.value))} ${shortUnit.value.toUpperCase()} IN`
                : `${imperial.value ? 'MILE' : 'KM'} ${toLen(frac * totalM.value).toFixed(1)}`)
            : null,
    };
}));

// The hazard marker a visitor is pointing at (or tapped, on a phone):
// its card floats over the map beside the marker. An SVG <title> was
// the browser's own delayed tooltip, and a phone never shows one.
const tipId = ref(null);
const tip = computed(() => {
    if (tipId.value == null) return null;
    const poi = pois.value.find((z) => z.tipKey === tipId.value);
    const m = (poi ? drawing.value?.pois : drawing.value?.hazards)?.find((z) => z.id === tipId.value);
    const h = poi || hazards.value.find((z) => z.tipKey === tipId.value);
    if (!m || !h) return null;
    const left = (m.x / W) * 100;
    // A pin's spot is its tip; the card opens from its head.
    const top = ((poi ? m.y - 30 : m.y) / H) * 100;
    return {
        ...h,
        isPoi: !!poi,
        detail: poi ? poi.description : hazardDetails(h).join(' · '),
        style: { left: `${left}%`, top: `${top}%` },
        // Opens downward near the top edge, and leans inward near a side.
        below: top < 38,
        align: left < 28 ? 'start' : left > 72 ? 'end' : 'mid',
    };
});
function showTip(id) { tipId.value = id; }
function hideTip(id) { if (tipId.value === id) tipId.value = null; }
// A tap OPENS (focus has usually opened it a moment before, so a toggle
// would close it again); a tap elsewhere or Escape closes it.
function toggleTip(id) { tipId.value = tipId.value === id ? null : id; }
function onDocTap(e) {
    if (tipId.value != null && !e.target.closest?.('.tdp-hazmark, .tdp-poimark, .tdp-tip')) tipId.value = null;
}
function onKey(e) { if (e.key === 'Escape') tipId.value = null; }
onMounted(() => { document.addEventListener('pointerdown', onDocTap); document.addEventListener('keydown', onKey); });
onBeforeUnmount(() => { document.removeEventListener('pointerdown', onDocTap); document.removeEventListener('keydown', onKey); });

const hov = ref(null);
function track(e) {
    const el = e.currentTarget;
    const r = el.getBoundingClientRect();
    const cx = e.touches?.[0]?.clientX ?? e.clientX;
    const frac = Math.max(0, Math.min(1, (cx - r.left) / r.width));
    const pts = profile.value?.points;
    if (!pts) return;
    const total = pts[pts.length - 1].d || 1;
    let best = 0;
    for (let i = 1; i < pts.length; i++) {
        if (Math.abs(pts[i].d / total - frac) < Math.abs(pts[best].d / total - frac)) best = i;
    }
    hov.value = best;
}
function untrack() { hov.value = null; }

const hovPoint = computed(() => {
    if (hov.value == null || !chart.value) return null;
    const q = profile.value.points[hov.value];
    const frac = q.d / (chart.value.total || 1);
    const ll = lngLatAt(frac);
    const onMap = ll && drawing.value ? drawing.value.project(ll[0], ll[1]) : null;
    return {
        pct: (frac * 100).toFixed(2),
        py: ((chart.value.y(q.e) / CH) * 100).toFixed(2),
        map: onMap,
        text: `${shortTrail.value ? `${fmtInt(toHeight(q.d))} ${shortUnit.value.toUpperCase()}` : `${lenUnit.value.toUpperCase()} ${toLen(q.d).toFixed(1)}`} · ${fmtInt(toHeight(q.e))} ${hUnit.value.toUpperCase()} · ${q.g >= 0 ? '+' : ''}${Math.round(q.g)}% GRADE`,
    };
});
const hovText = computed(() => hovPoint.value?.text || 'Hover the profile to trace the route');

// ── Specs and details ────────────────────────────────────────────
// The hairline grid joined under the map, then the words about the
// trail. A figure the trail has no stored number for is left out
// rather than printed as a dash.
const sentenceCase = (v) => {
    const w = String(v || '').replace(/[_-]+/g, ' ').trim();
    return w ? w.charAt(0).toUpperCase() + w.slice(1) : '';
};
const specs = computed(() => {
    const out = [];
    const d = t.value?.distance_m ?? (lineTotal.value || null);
    if (d) out.push({ k: 'Distance', ...fmtLen(d) });
    const height = (k, label) => {
        const m = t.value?.[k];
        if (m != null) out.push({ k: label, v: fmtInt(toHeight(m)), u: hUnit.value });
    };
    height('elevation_gain_m', 'Elev gain');
    height('elevation_loss_m', 'Elev loss');
    if (t.value?.grade_max_pct != null) out.push({ k: 'Max grade', v: `${Number(t.value.grade_max_pct).toFixed(1)}%`, u: '' });
    if (t.value?.grade_avg_pct != null) out.push({ k: 'Avg grade', v: `${Number(t.value.grade_avg_pct).toFixed(1)}%`, u: '' });
    if (t.value?.aspect) out.push({ k: 'Aspect', v: t.value.aspect, u: '' });
    return out;
});

const ACCESS_LABELS = { public: 'Public', private: 'Private', pass_required: 'Pass required' };
const details = computed(() => {
    const out = [];
    if (difficultyLabel.value) out.push({ k: 'Difficulty', v: difficultyLabel.value, n: '', icon: true });
    const types = (t.value?.trail_types || []).map((k) => trailType(k)?.label).filter(Boolean);
    if (types.length) out.push({ k: types.length > 1 ? 'Types' : 'Type', v: types.join(', '), n: '' });
    if (directionLabel(t.value?.direction)) out.push({ k: 'Direction', v: directionLabel(t.value.direction), n: '' });
    const b = t.value?.bikes;
    if (b) {
        const e = b.allowed && b.pedal_assist && b.throttle ? 'e-bikes ok'
            : b.allowed && b.pedal_assist ? 'pedal-assist e-bikes ok'
                : b.allowed && b.throttle ? 'throttle e-bikes ok' : '';
        out.push({ k: 'Bikes', v: b.allowed ? 'Allowed' : 'Not allowed', n: e ? ` · ${e}` : '' });
    }
    if (t.value?.surface) out.push({ k: 'Surface', v: sentenceCase(t.value.surface), n: '' });
    if (t.value?.access) out.push({ k: 'Access', v: ACCESS_LABELS[t.value.access] || sentenceCase(t.value.access), n: '' });
    if (t.value?.uses?.[0]) out.push({ k: 'Primary use', v: t.value.uses[0], n: '' });
    return out;
});
const alsoAllowed = computed(() => (t.value?.uses || []).slice(1));
// What a visitor meets on it (TrailCatalog), each with the sentence
// the Trails page's Features block uses. Horse access is already said
// by the uses above, so it is not repeated here.
const featureList = computed(() => (t.value?.features || [])
    .filter((k) => k !== 'horse_access').map((k) => trailFeature(k)).filter(Boolean));

// In part mode, whether this part has anything to draw — a block with
// nothing in it should take no room on the page.
const hasPart = computed(() => {
    if (!t.value) return false;
    switch (props.part) {
        case 'header': return true;
        case 'map': return !!(drawing.value || chart.value || specs.value.length);
        case 'details': return !!(details.value.length || alsoAllowed.value.length || pois.value.length);
        case 'about': return !!(t.value.description || status.value);
        case 'weather': return !!wxView.value;
        // No box at all when nothing is reported: an empty box reads
        // as a feature that is not working.
        case 'hazards': return hazards.value.length > 0;
        case 'nearby': return !!(featureList.value.length || t.value.network || t.value.uses?.length || nearby.value.length || t.value.report_url);
        default: return true;
    }
});

// ── Trail weather ────────────────────────────────────────────────
// SiteTrailWeather: today is the advisor's number (the one on the
// trail cards), the days around it the same score asked about those
// days. Thresholds are the trail's own, for its surface, drainage and
// use. The threshold is a fact about the number and always shows; the
// verdict (the good / caution / too wet pill, "please stay off") is
// status, and with public status off it is left out.
const wx = computed(() => t.value?.weather || null);
const wxStatus = computed(() => Boolean(t.value?.show_status && wx.value?.recommendation));
const toTemp = (c) => (c == null ? null : imperial.value ? `${Math.round((c * 9) / 5 + 32)}°F` : `${Math.round(c)}°C`);
const rainUnit = computed(() => (imperial.value ? 'in' : 'mm'));
const toRain = (mm) => (imperial.value ? mm / 25.4 : mm);
const rainText = (mm) => (mm == null ? '—' : imperial.value ? `${toRain(mm).toFixed(2)} in` : `${Math.round(mm * 10) / 10} mm`);
const rainShort = (mm) => (!mm ? '' : imperial.value ? toRain(mm).toFixed(2).replace(/^0/, '') : String(Math.round(mm)));

const WX_PILLS = {
    open: { label: 'good', cls: 'open' },
    caution: { label: 'caution', cls: 'caution' },
    closed: { label: 'too wet', cls: 'closed' },
};
const wxView = computed(() => {
    const w = wx.value;
    if (!w) return null;
    const now = w.wetness_pct;
    const close = Number(w.close_pct) || 50;
    const caution = Number(w.caution_pct) || close * 0.65;
    const peak = Math.max(now, ...w.days.map((d) => d.wetness_pct ?? 0));
    // The scale: room above the threshold, and above the wettest day.
    const max = Math.ceil(Math.max(close * 1.6, peak * 1.1) / 10) * 10;
    const xs = (i) => ((i + 0.5) / w.days.length) * 100;
    const ys = (v) => 100 - (Math.min(v, max) / max) * 100;
    const todayIdx = Math.max(0, w.days.findIndex((d) => d.when === 'today'));
    const pt = (d, i) => (d.wetness_pct == null ? null : `${xs(i).toFixed(2)},${ys(d.wetness_pct).toFixed(2)}`);
    const pastPts = w.days.slice(0, todayIdx + 1).map(pt).filter(Boolean);
    const aheadPts = w.days.slice(todayIdx).map((d, i) => pt(d, i + todayIdx)).filter(Boolean);
    const pastIdx = w.days.slice(0, todayIdx + 1).map((d, i) => (d.wetness_pct == null ? null : i)).filter((i) => i != null);
    const maxRain = Math.max(12, ...w.days.map((d) => d.rain_mm || 0));

    const use = (t.value?.uses?.[0] || 'trail').toLowerCase();
    let tail = now >= close ? `Above the closure threshold.${wxStatus.value ? ' Please stay off until it dries.' : ''}`
        : now >= caution ? `Approaching the closure threshold for ${use} use.`
            : 'Well below the closure threshold.';
    if (wxStatus.value) {
        if (w.recommendation === 'closed' && now < close) tail += ' Heavy rain on the way puts it at closed.';
        else if (w.recommendation === 'caution' && now < caution) tail += ' Rain on the way puts it at caution.';
    }
    const ground = [
        t.value?.surface ? `on ${String(t.value.surface).replace(/_/g, ' ')} surface` : '',
        t.value?.drainage ? `with ${t.value.drainage} drainage` : '',
    ].filter(Boolean).join(' ');
    const summary = `Trail wetness at ${now}% (from ${rainText(w.rain_5d_mm)} of rain over the last five days)${ground ? ` ${ground}` : ''}. ${tail}`.trim();

    return {
        now,
        trend: w.trend ? w.trend.toUpperCase() : '',
        pill: wxStatus.value ? WX_PILLS[w.recommendation] || null : null,
        close,
        caution,
        max,
        gauge: Math.min((now / max) * 100, 100).toFixed(1),
        cautionPct: ((caution / max) * 100).toFixed(1),
        closePct: ((close / max) * 100).toFixed(1),
        summary,
        thrTop: (100 - (close / max) * 100).toFixed(1),
        todayX: xs(todayIdx).toFixed(2),
        todayY: (100 - ys(now)).toFixed(2),
        line: pastPts.join(' '),
        lineAhead: aheadPts.length > 1 ? aheadPts.join(' ') : '',
        area: pastIdx.length > 1
            ? `${xs(pastIdx[0]).toFixed(2)},100 ${pastPts.join(' ')} ${xs(pastIdx[pastIdx.length - 1]).toFixed(2)},100`
            : '',
        cols: w.days.map((d, i) => {
            const day = parseCalendarDate(d.date) || new Date();
            return {
                key: d.date,
                d: day.toLocaleDateString('en-US', { weekday: 'short' }).slice(0, 2).toUpperCase(),
                today: d.when === 'today',
                ahead: d.when === 'ahead',
                w: d.wetness_pct == null ? '—' : `${d.wetness_pct}%`,
                over: d.wetness_pct != null && d.wetness_pct >= close,
                mm: d.rain_mm == null ? 'No data' : rainText(d.rain_mm),
                mmS: rainShort(d.rain_mm),
                rain: d.rain_mm ? Math.max(4, (d.rain_mm / maxRain) * 100).toFixed(1) : 0,
                idx: i,
            };
        }),
        readings: [
            { k: 'Soil temp', v: toTemp(w.soil_temp_c) || '—' },
            { k: "Today's high", v: toTemp(w.high_c) || '—' },
            { k: 'Rain 24h', v: rainText(w.rain_24h_mm) },
            { k: 'Rain 72h', v: rainText(w.rain_72h_mm) },
        ],
        updated: wxUpdated(w.updated_at),
        network: w.network || '',
    };
});
function wxUpdated(iso) {
    if (!iso) return '';
    const at = new Date(iso);
    if (Number.isNaN(at.getTime())) return '';
    const mins = Math.max(0, Math.round((Date.now() - at.getTime()) / 60000));
    if (mins < 60) return mins <= 1 ? 'Updated just now' : `Updated ${mins} minutes ago`;
    const hrs = Math.round(mins / 60);
    if (hrs < 24) return `Updated ${hrs} hour${hrs === 1 ? '' : 's'} ago`;
    return `Updated ${at.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}`;
}

const startLabel = computed(() => (t.value?.loop ? 'Trailhead' : 'Start'));
</script>

<template>
    <section v-if="t && (!part || hasPart)" class="tdp" :class="{ 'tdp--part': part, [`tdp--part-${part}`]: part }" :id="config.anchor_id || null">
        <div v-if="on('header')" class="tdp-wrap tdp-head">
            <a class="tdp-back tdp-mono" :href="backHref">← Back</a>
            <div class="tdp-titlerow">
                <h1 class="tdp-h1">{{ t.name }}</h1>
                <span v-if="status" class="tdp-pill" :class="`tdp-pill--${status.tone}`"><span class="tdp-dot"></span>{{ status.label }}</span>
            </div>
            <div v-if="metaLine" class="tdp-mono tdp-meta">
                <span v-if="difficultyLabel" class="tdp-diff">
                    <DifficultyIcon v-if="difficultyIcon" :difficulty="difficultyIcon.difficulty" :icon-set="difficultyIcon.set"
                                    :color="difficultyIcon.color" class="tdp-diff__icon" aria-hidden="true" />{{ difficultyLabel }}</span><template
                    v-if="difficultyLabel && metaRest"> · </template>{{ metaRest }}
            </div>
        </div>

        <div v-if="on('map') && (drawing || chart || specs.length)" class="tdp-wrap">
            <div v-if="drawing || chart" class="tdp-mapcard" :class="{ 'tdp-mapcard--joined': specs.length }">
                <div v-if="drawing" class="tdp-map">
                    <svg :viewBox="`0 0 ${W} ${H}`" preserveAspectRatio="xMidYMid meet" class="tdp-map__svg" role="img" :aria-label="`Map of ${t.name}`">
                        <g v-if="drawing.contours" class="tdp-contours" aria-hidden="true">
                            <path :d="drawing.contours.minor" fill="none" stroke="#cfc8b0" stroke-width="1.2" stroke-linejoin="round" />
                            <path :d="drawing.contours.index" fill="none" stroke="#b7ab8a" stroke-width="2.2" stroke-linejoin="round" />
                            <text v-for="(l, i) in drawing.contours.labels" :key="`cl-${i}`"
                                  :transform="`translate(${l.x.toFixed(1)} ${l.y.toFixed(1)}) rotate(${l.angle.toFixed(1)})`"
                                  text-anchor="middle" dominant-baseline="central" class="tdp-contourtext">{{ l.text }}</text>
                        </g>
                        <template v-else>
                            <path v-for="(g, i) in drawing.grid" :key="i" :d="g" fill="none" stroke="#d5d9c8" stroke-width="1.5" />
                        </template>
                        <path :d="drawing.route" fill="none" stroke="#fff" stroke-width="10" stroke-linecap="round" stroke-linejoin="round" />
                        <path :d="drawing.route" fill="none" stroke="var(--tdp-route, var(--accent))" stroke-width="4" stroke-linecap="round" stroke-linejoin="round" />
                        <rect v-if="!t.loop" :x="drawing.end.x - 9" :y="drawing.end.y - 9" width="18" height="18" fill="#fff" stroke="#1b1d1a" stroke-width="3" />
                        <circle :cx="drawing.start.x" :cy="drawing.start.y" r="10" fill="#1b1d1a" stroke="#fff" stroke-width="3" />
                        <g v-for="z in drawing.pois" :key="z.id" :transform="`translate(${z.x} ${z.y})`"
                           class="tdp-poimark" :class="{ 'is-active': tipId === z.id }"
                           tabindex="0" role="button" :aria-label="z.label" :aria-expanded="tipId === z.id"
                           @pointerenter="$event.pointerType === 'mouse' && showTip(z.id)" @pointerleave="$event.pointerType === 'mouse' && hideTip(z.id)"
                           @focus="showTip(z.id)" @blur="hideTip(z.id)"
                           @click="showTip(z.id)" @keydown.enter.prevent="toggleTip(z.id)">
                            <circle r="24" cy="-24" fill="transparent" />
                            <path class="tdp-poimark__pin" d="M0 -40C-9.4 -40 -17 -32.4 -17 -23C-17 -11.2 0 0 0 0S17 -11.2 17 -23C17 -32.4 9.4 -40 0 -40Z" fill="#1f5f74" stroke="#fff" stroke-width="3" stroke-linejoin="round" />
                            <g transform="translate(-9 -32) scale(.75)" fill="none" stroke="#fff" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round" v-html="z.glyph"></g>
                        </g>
                        <g v-for="z in drawing.hazards" :key="z.id" :transform="`translate(${z.x} ${z.y})`"
                           class="tdp-hazmark" :class="{ 'is-active': tipId === z.id }"
                           tabindex="0" role="button" :aria-label="z.label" :aria-expanded="tipId === z.id"
                           @pointerenter="$event.pointerType === 'mouse' && showTip(z.id)" @pointerleave="$event.pointerType === 'mouse' && hideTip(z.id)"
                           @focus="showTip(z.id)" @blur="hideTip(z.id)"
                           @click="showTip(z.id)" @keydown.enter.prevent="toggleTip(z.id)">
                            <circle r="26" cy="-3" fill="transparent" />
                            <path d="M0 -20 L17 10 L-17 10 Z" :fill="z.fill" stroke="#fff" stroke-width="3" stroke-linejoin="round" />
                            <path d="M0 -9 V0 M0 4.5 V5" stroke="#fff" stroke-width="3" stroke-linecap="round" />
                        </g>
                        <circle v-if="hovPoint?.map" :cx="hovPoint.map.x" :cy="hovPoint.map.y" r="12" fill="var(--tdp-route, var(--accent))" stroke="#fff" stroke-width="4" />
                        <!-- Compass and scale bar, as on the /t page. -->
                        <g :transform="`translate(${W - 44} 44)`" class="tdp-compass">
                            <circle r="22" fill="rgba(255,255,255,.92)" stroke="#d5d9c8" stroke-width="1.5" />
                            <path d="M0 -15 L6 3 L0 0 L-6 3 Z" fill="#1b1d1a" />
                            <path d="M0 15 L6 -3 L0 0 L-6 -3 Z" fill="#b9bdb0" />
                            <text y="-26" text-anchor="middle" class="tdp-svgtext">N</text>
                        </g>
                        <g :transform="`translate(${W - 24 - drawing.scale.width} ${H - 26})`">
                            <rect x="-10" y="-22" :width="drawing.scale.width + 20" height="36" rx="4" fill="rgba(255,255,255,.92)" />
                            <path :d="`M0 0H${drawing.scale.width.toFixed(1)}M0 -6V6M${drawing.scale.width.toFixed(1)} -6V6`" stroke="#1b1d1a" stroke-width="2.5" />
                            <text :x="drawing.scale.width / 2" y="-9" text-anchor="middle" class="tdp-svgtext">{{ drawing.scale.label }}</text>
                        </g>
                    </svg>
                    <div v-if="tip" class="tdp-tip" :class="[`tdp-tip--${tip.align}`, { 'tdp-tip--below': tip.below }]" :style="tip.style" role="tooltip">
                        <template v-if="tip.isPoi">
                            <div class="tdp-tip__top"><span class="tdp-mono tdp-tip__sev tdp-tip__sev--poi">{{ tip.type }}</span></div>
                            <div class="tdp-tip__title">{{ tip.name }}</div>
                            <div v-if="tip.detail" class="tdp-tip__detail">{{ tip.detail }}</div>
                        </template>
                        <template v-else>
                        <div class="tdp-tip__top">
                            <span class="tdp-mono tdp-tip__sev" :style="{ background: tip.tone.bg, color: tip.tone.fg }">{{ tip.level_label }}</span>
                            <span v-if="tip.along" class="tdp-mono tdp-tip__along">{{ tip.along }}</span>
                        </div>
                        <div class="tdp-tip__title"><HazardIcon :hazard="tip" :size="14" class="tdp-haz__type" />{{ tip.title }}</div>
                        <div v-if="tip.detail" class="tdp-tip__detail">{{ tip.detail }}</div>
                        </template>
                    </div>
                    <div class="tdp-legend tdp-mono">
                        <span><span class="tdp-legend__start"></span>{{ startLabel }}</span>
                        <span v-if="!t.loop"><span class="tdp-legend__end"></span>Finish</span>
                        <span v-if="drawing.pois.length"><span class="tdp-legend__poi"></span>Point of interest</span>
                        <span v-if="drawing.hazards.length"><span class="tdp-legend__haz"></span>Hazard</span>
                    </div>
                </div>

                <div v-if="chart" class="tdp-elev" :class="{ 'tdp-elev--alone': !drawing }">
                    <div class="tdp-elev__head">
                        <div class="tdp-mono tdp-label">Elevation profile</div>
                        <div class="tdp-mono tdp-elev__hov">{{ hovText }}</div>
                    </div>
                    <div class="tdp-elev__plot">
                        <div class="tdp-mono tdp-elev__axis tdp-elev__axis--max">{{ chart.maxLabel }}</div>
                        <div class="tdp-mono tdp-elev__axis tdp-elev__axis--min">{{ chart.minLabel }}</div>
                        <div class="tdp-elev__area"
                             @mousemove="track" @touchstart.passive="track" @touchmove.passive="track" @mouseleave="untrack">
                            <svg :viewBox="`0 0 1000 ${CH}`" preserveAspectRatio="none" class="tdp-elev__svg" aria-hidden="true">
                                <path :d="chart.area" class="tdp-elev__fill" />
                                <path :d="chart.line" fill="none" stroke="var(--tdp-route, var(--accent))" stroke-width="2" vector-effect="non-scaling-stroke" />
                            </svg>
                            <template v-for="z in hazards" :key="`hz-${z.tipKey}`">
                                <template v-if="z.frac != null">
                                    <div class="tdp-elev__hz" :style="{ left: `${z.frac * 100}%`, borderColor: z.tone.bg }"></div>
                                    <div class="tdp-mono tdp-elev__hzlabel" :style="{ left: `${z.frac * 100}%`, background: z.tone.bg, color: z.tone.fg }"><HazardIcon :hazard="z" :size="11" class="tdp-elev__hzicon" />{{ z.level_label }}</div>
                                </template>
                            </template>
                            <template v-if="hovPoint">
                                <div class="tdp-elev__cross" :style="{ left: `${hovPoint.pct}%` }"></div>
                                <div class="tdp-elev__dot" :style="{ left: `${hovPoint.pct}%`, top: `${hovPoint.py}%` }"></div>
                            </template>
                        </div>
                        <div class="tdp-elev__ticks">
                            <span v-for="k in chart.ticks" :key="k.pct" class="tdp-mono" :style="{ left: `${k.pct}%` }">{{ k.label }}</span>
                        </div>
                    </div>
                </div>
            </div>
            <!-- The design's hairline grid, joined under the map card:
                 six across, three under 1000px, two under 560px. -->
            <div v-if="specs.length" class="tdp-specs" :class="{ 'tdp-specs--alone': !(drawing || chart) }">
                <div v-for="sp in specs" :key="sp.k" class="tdp-spec">
                    <div class="tdp-mono tdp-spec__k">{{ sp.k }}</div>
                    <div class="tdp-spec__row"><span class="tdp-spec__v">{{ sp.v }}</span><span v-if="sp.u" class="tdp-spec__u">{{ sp.u }}</span></div>
                </div>
            </div>
        </div>

        <div v-if="!part || !['header', 'map'].includes(part)" class="tdp-wrap tdp-body">
            <div v-if="on('details') && (details.length || alsoAllowed.length || pois.length)" class="tdp-details">
                <div v-if="details.length" class="tdp-details__grid">
                    <div v-for="d in details" :key="d.k" class="tdp-detail">
                        <div class="tdp-mono tdp-spec__k">{{ d.k }}</div>
                        <div class="tdp-detail__v"><span v-if="d.icon && difficultyIcon" class="tdp-diff"><DifficultyIcon :difficulty="difficultyIcon.difficulty" :icon-set="difficultyIcon.set"
                            :color="difficultyIcon.color" class="tdp-diff__icon tdp-diff__icon--lg" aria-hidden="true" />{{ d.v }}</span><template v-else>{{ d.v }}</template><span v-if="d.n" class="tdp-detail__n">{{ d.n }}</span></div>
                    </div>
                </div>
                <div v-if="alsoAllowed.length" class="tdp-also">
                    <div class="tdp-mono tdp-spec__k tdp-also__k">Also allowed</div>
                    <span v-for="a in alsoAllowed" :key="a" class="tdp-also__chip">{{ a }}</span>
                </div>
                <!-- The points of interest tied to the trail, the same
                     ones pinned on the map. -->
                <div v-if="pois.length" class="tdp-feat">
                    <div class="tdp-feat__box" data-trail-pois>
                        <h2 class="tdp-h3">{{ heading('Points of interest', 'pois_title') }}</h2>
                        <ul class="tdp-feat__list">
                            <li v-for="q in pois" :key="q.tipKey" class="tdp-feat__row tdp-feat__row--poi">
                                <svg class="tdp-feat__glyph" width="30" height="30" viewBox="-3 -3 30 30" aria-hidden="true">
                                    <circle cx="12" cy="12" r="15" fill="#1f5f74" />
                                    <g transform="translate(3 3) scale(.75)" fill="none" stroke="#fff" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round" v-html="q.glyph"></g>
                                </svg>
                                <span class="tdp-feat__text">
                                    <span class="tdp-feat__top"><span class="tdp-feat__name">{{ q.name }}</span><span class="tdp-mono tdp-feat__type">{{ q.type }}</span></span>
                                    <span v-if="q.description" class="tdp-feat__desc">{{ q.description }}</span>
                                </span>
                            </li>
                        </ul>
                    </div>
                </div>
            </div>

            <div class="tdp-cols">
                <div class="tdp-main">
                    <div v-if="on('about') && t.description" class="tdp-part">
                        <h2 class="tdp-h2">{{ heading('About this trail') }}</h2>
                        <p class="tdp-lede">{{ t.description }}</p>
                    </div>

                    <div v-if="on('about') && status" class="tdp-part">
                        <h2 class="tdp-h2">{{ heading('Current conditions', 'conditions_title') }}</h2>
                        <div class="tdp-cond">
                            <div class="tdp-cond__top">
                                <span class="tdp-pill" :class="`tdp-pill--${status.tone}`"><span class="tdp-dot"></span>{{ status.label }}</span>
                                <span v-if="conditionWords && conditionWords !== status.label" class="tdp-cond__name">{{ conditionWords }}</span>
                            </div>
                            <p v-if="t.condition_note" class="tdp-cond__note">{{ t.condition_note }}</p>
                            <div v-if="updatedLabel" class="tdp-mono tdp-faint">{{ updatedLabel }}</div>
                        </div>
                    </div>

                    <div v-if="on('weather') && wxView" class="tdp-wx">
                        <h2 class="tdp-h2">{{ heading('Trail weather') }}</h2>
                        <div class="tdp-wx__card">
                            <div class="tdp-wx__top">
                                <div class="tdp-wx__headrow">
                                    <div class="tdp-wx__now">
                                        <div class="tdp-mono tdp-spec__k">Trail wetness<template v-if="wxView.trend"> · {{ wxView.trend }}</template></div>
                                        <div class="tdp-wx__big">{{ wxView.now }}%</div>
                                    </div>
                                    <span v-if="wxView.pill" class="tdp-wx__pill" :class="`tdp-wx__pill--${wxView.pill.cls}`">{{ wxView.pill.label }}</span>
                                </div>
                                <div class="tdp-wx__gaugewrap">
                                    <div class="tdp-wx__gauge">
                                        <div class="tdp-wx__zone tdp-wx__zone--good" :style="{ width: `${wxView.cautionPct}%` }"></div>
                                        <div class="tdp-wx__zone tdp-wx__zone--caution" :style="{ width: `${wxView.closePct - wxView.cautionPct}%` }"></div>
                                        <div class="tdp-wx__zone tdp-wx__zone--closed"></div>
                                        <div class="tdp-wx__knob" :style="{ left: `${wxView.gauge}%` }"></div>
                                    </div>
                                    <div class="tdp-wx__scale">
                                        <span style="left: 0">Dry</span>
                                        <span class="is-mid" :style="{ left: `${wxView.cautionPct}%` }">{{ Math.round(wxView.caution) }}%</span>
                                        <span class="is-mid is-close" :style="{ left: `${wxView.closePct}%` }">{{ Math.round(wxView.close) }}% close</span>
                                        <span style="right: 0">{{ wxView.max }}%+</span>
                                    </div>
                                </div>
                                <p class="tdp-wx__summary">{{ wxView.summary }}</p>
                            </div>
                            <div class="tdp-wx__chartpart">
                                <div class="tdp-wx__charthead">
                                    <span class="tdp-mono tdp-spec__k">Last 5 days + 2-day forecast</span>
                                    <div class="tdp-wx__legend">
                                        <span><span class="tdp-wx__lg-line"></span>Wetness</span>
                                        <span><span class="tdp-wx__lg-dash"></span>Forecast</span>
                                        <span><span class="tdp-wx__lg-bar"></span>Rain {{ rainUnit }}</span>
                                    </div>
                                </div>
                                <div class="tdp-wx__chart">
                                    <div class="tdp-wx__over" :style="{ height: `${wxView.thrTop}%` }"></div>
                                    <div class="tdp-wx__thr" :style="{ top: `${wxView.thrTop}%` }"><span>Closure threshold</span></div>
                                    <div class="tdp-wx__nowline" :style="{ left: `${wxView.todayX}%` }"><span>Now</span></div>
                                    <svg viewBox="0 0 100 100" preserveAspectRatio="none" class="tdp-wx__svg" aria-hidden="true">
                                        <polygon v-if="wxView.area" :points="wxView.area" class="tdp-wx__area" />
                                        <polyline v-if="wxView.line" :points="wxView.line" class="tdp-wx__line" />
                                        <polyline v-if="wxView.lineAhead" :points="wxView.lineAhead" class="tdp-wx__line tdp-wx__line--ahead" />
                                    </svg>
                                    <div class="tdp-wx__today" :style="{ left: `${wxView.todayX}%`, bottom: `${wxView.todayY}%` }"></div>
                                </div>
                                <div class="tdp-wx__bars">
                                    <div v-for="c in wxView.cols" :key="c.key" class="tdp-wx__barcol" :title="c.mm">
                                        <span>{{ c.mmS }}</span>
                                        <div class="tdp-wx__bar" :class="{ 'tdp-wx__bar--ahead': c.ahead }" :style="{ height: `${c.rain}%` }"></div>
                                    </div>
                                </div>
                                <div class="tdp-wx__days">
                                    <div v-for="c in wxView.cols" :key="c.key" class="tdp-wx__day" :class="{ 'is-today': c.today }">
                                        <span class="tdp-wx__dname">{{ c.d }}</span>
                                        <span class="tdp-wx__dval" :class="{ 'is-over': c.over }">{{ c.w }}</span>
                                    </div>
                                </div>
                            </div>
                            <div class="tdp-wx__readings">
                                <div v-for="r in wxView.readings" :key="r.k" class="tdp-wx__reading">
                                    <div class="tdp-mono tdp-spec__k">{{ r.k }}</div>
                                    <div class="tdp-wx__rv">{{ r.v }}</div>
                                </div>
                            </div>
                            <div v-if="wxView.updated || wxView.network" class="tdp-mono tdp-wx__foot">{{ [wxView.updated, wxView.network].filter(Boolean).join(' · ') }}</div>
                        </div>
                    </div>
                </div>

                <aside class="tdp-aside">
                    <div v-if="on('hazards') && hazards.length" class="tdp-part tdp-part--haz">
                        <div class="tdp-part__head">
                            <h2 class="tdp-h3">{{ heading('Hazards') }}</h2>
                            <a v-if="t.report_url" :href="t.report_url" class="tdp-more">Report a hazard →</a>
                        </div>
                        <div v-for="z in hazards" :key="z.tipKey" class="tdp-haz" :style="{ background: z.tone.soft }">
                            <svg width="22" height="22" viewBox="0 0 24 24" class="tdp-haz__icon" aria-hidden="true">
                                <path d="M12 3 L22 20 H2 Z" :fill="z.tone.bg" stroke-linejoin="round" />
                                <path d="M12 9.5 V14 M12 17 V17.2" :stroke="z.tone.fg" stroke-width="2.2" stroke-linecap="round" />
                            </svg>
                            <div class="tdp-haz__text">
                                <div class="tdp-haz__top">
                                    <span class="tdp-haz__title"><HazardIcon :hazard="z" :size="15" class="tdp-haz__type" />{{ z.title }}</span>
                                    <span class="tdp-mono tdp-haz__sev" :style="{ color: z.tone.ink }">{{ [z.level_label, z.along].filter(Boolean).join(' · ') }}</span>
                                </div>
                                <div v-if="z.subject || z.detail || z.passable_label" class="tdp-haz__detail">{{ [z.subject, z.detail, z.passable_label].filter(Boolean).join(' · ') }}</div>
                            </div>
                            <div v-if="z.reported_on" class="tdp-mono tdp-haz__by">
                                <span>{{ z.reported_verb || 'Reported' }}</span>
                                <span class="tdp-haz__when">{{ reportedDay(z.reported_on) }}</span>
                            </div>
                        </div>
                        <div v-if="!hazards.length" class="tdp-nohaz"><span class="tdp-nohaz__dot"></span>No hazards reported on this trail.</div>
                    </div>
                    <!-- What a visitor meets on the trail, in the right
                         column under the hazards. In a website built from
                         the trail template it rides the Network and nearby
                         block, which is that column's. -->
                    <div v-if="on('nearby') && featureList.length" class="tdp-part" data-trail-features>
                        <h2 class="tdp-h3">{{ heading('Trail features', 'features_title') }}</h2>
                        <ul class="tdp-feat__box tdp-feat__list">
                            <li v-for="f in featureList" :key="f.key" class="tdp-feat__row">
                                <span class="tdp-feat__name">{{ f.label }}</span>
                                <span v-if="f.description" class="tdp-feat__desc">{{ f.description }}</span>
                            </li>
                        </ul>
                    </div>
                    <div v-if="on('nearby') && (t.network || (t.uses && t.uses.length))" class="tdp-net">
                        <div class="tdp-mono tdp-label">Network</div>
                        <div v-if="t.network" class="tdp-net__name"><a v-if="t.network_href" :href="t.network_href" class="tdp-net__link" data-network-link>{{ t.network }}</a><template v-else>{{ t.network }}</template></div>
                        <div v-if="t.uses && t.uses.length" class="tdp-net__uses">
                            <span v-for="u in t.uses" :key="u" class="tdp-mono tdp-chip">{{ u }}</span>
                        </div>
                    </div>
                    <div v-if="on('nearby') && nearby.length" class="tdp-part">
                        <h2 class="tdp-h3">{{ heading('Trails nearby') }}</h2>
                        <div class="tdp-near">
                            <a v-for="n in nearby" :key="n.slug" :href="n.href" class="tdp-near__row">
                                <span v-if="n.dot" class="tdp-near__dot" :style="{ background: n.dot }" :title="n.statusLabel"></span>
                                <DifficultyIcon v-else-if="n.icon" :difficulty="n.icon.difficulty" :icon-set="n.icon.set" :color="n.icon.color" class="tdp-near__icon" aria-hidden="true" />
                                <span class="tdp-near__text">
                                    <span class="tdp-near__name">{{ n.name }}</span>
                                    <span class="tdp-mono tdp-near__meta">{{ n.meta }}</span>
                                </span>
                                <span class="tdp-near__go" aria-hidden="true">→</span>
                            </a>
                        </div>
                    </div>
                    <a v-if="on('nearby') && t.report_url" :href="t.report_url" class="tdp-more">Report a problem on this trail →</a>
                </aside>
            </div>
        </div>
    </section>
</template>

<style scoped>
.tdp {
    --tdp-ink: var(--th-ink, #1b1d1a);
    --tdp-line: var(--th-line, #e3e1da);
    --tdp-rule: var(--th-rule, #d9d6cd);
    --tdp-meta: var(--th-meta, #5c6159);
    --tdp-muted: var(--th-muted, #4d524b);
    --tdp-mono: var(--th-mono, ui-monospace, 'SFMono-Regular', Menlo, monospace);
    /* A real family in every fallback: `inherit` inside a font
       shorthand voids the whole declaration, and the headings came
       out at body size on Classic. */
    --tdp-display: var(--th-display, 'Barlow', system-ui, sans-serif);
    color: var(--tdp-ink);
}
.tdp-wrap {
    max-width: 1240px;
    margin: 0 auto;
    padding-left: clamp(16px, 4vw, 40px);
    padding-right: clamp(16px, 4vw, 40px);
}
.tdp-mono { font: 500 11px/1.5 var(--tdp-mono); letter-spacing: .1em; text-transform: uppercase; }
.tdp-label { color: var(--tdp-meta); font-size: 10px; }
.tdp-faint { color: #7a7e76; font-size: 10px; }

.tdp-head { padding-top: clamp(24px, 3vw, 36px); padding-bottom: 20px; }
/* Classic's menu is fixed over the top of the page (68px), and its
   own page header clears it the same way. */
/* Under the classic template's fixed nav. NOT `:global(.tpl-classic) .tdp-head`:
   Vue compiles that to a bare `.tpl-classic { … }` and padded every
   page of a classic site once this chunk loaded. The scope attribute
   lands on the last compound only, so a plain ancestor works. */
.tpl-classic .tdp-head { padding-top: calc(68px + clamp(24px, 3vw, 36px)); }
.tdp-back { display: inline-block; color: var(--tdp-meta); margin-bottom: 18px; text-decoration: none; }
.tdp-back:hover { color: var(--tdp-ink); }
.tdp-titlerow { display: flex; flex-wrap: wrap; align-items: center; gap: 10px 16px; }
.tdp-h1 {
    font: 800 clamp(34px, 5vw, 60px)/1 var(--tdp-display);
    font-stretch: 110%;
    letter-spacing: -.02em;
    margin: 0;
    overflow-wrap: anywhere;
}
.tdp-meta { color: var(--tdp-meta); margin-top: 14px; }

.tdp-pill {
    display: inline-flex; align-items: center; gap: 6px;
    padding: 6px 10px; border-radius: 999px;
    font: 500 11px/1 var(--tdp-mono); letter-spacing: .1em; text-transform: uppercase;
}
.tdp-dot { width: 7px; height: 7px; border-radius: 50%; background: currentColor; }
.tdp-pill--open { background: #e3f1e6; color: #1d6b35; }
.tdp-pill--open .tdp-dot { background: #2e9b4f; }
.tdp-pill--caution { background: #fbf0d9; color: #7d5200; }
.tdp-pill--caution .tdp-dot { background: #d99a1e; }
.tdp-pill--closed { background: #f8e2df; color: #9b2a1f; }
.tdp-pill--closed .tdp-dot { background: #d2452f; }

.tdp-mapcard { border: 1px solid var(--tdp-line); border-radius: var(--r, 6px); overflow: hidden; background: #fff; }
.tdp-map { position: relative; aspect-ratio: 2 / 1; background: #eef0e6; overflow: hidden; }
/* Taller on a phone, never a min-height: with aspect-ratio a min-height
   widens the box past the card, and the drawing is cropped at the
   right — the finish, the compass and the scale bar with it. */
@media (max-width: 640px) { .tdp-map { aspect-ratio: 4 / 3; } }
.tdp-hazmark { cursor: pointer; outline: none; }
.tdp-hazmark path:first-of-type { transition: transform .12s; transform-origin: 0 0; }
.tdp-hazmark:hover path:first-of-type, .tdp-hazmark.is-active path:first-of-type { transform: scale(1.12); }
.tdp-hazmark:focus-visible path:first-of-type { stroke: var(--accent); }
/* A marker's card: above the marker, its tail on the marker's tip. */
.tdp-tip {
    position: absolute;
    z-index: 3;
    width: max-content;
    max-width: min(280px, 78%);
    transform: translate(-50%, calc(-100% - 24px));
    background: #fff;
    border: 1px solid var(--tdp-line);
    border-radius: var(--r, 6px);
    box-shadow: 0 6px 22px rgba(27, 29, 26, .16);
    padding: 12px 14px;
    display: flex;
    flex-direction: column;
    gap: 6px;
    pointer-events: none;
}
.tdp-tip--start { transform: translate(-22px, calc(-100% - 24px)); }
.tdp-tip--end { transform: translate(calc(-100% + 22px), calc(-100% - 24px)); }
.tdp-tip--below { transform: translate(-50%, 18px); }
.tdp-tip--below.tdp-tip--start { transform: translate(-22px, 18px); }
.tdp-tip--below.tdp-tip--end { transform: translate(calc(-100% + 22px), 18px); }
.tdp-tip__top { display: flex; align-items: center; gap: 8px; }
.tdp-tip__sev { font-size: 10px; letter-spacing: .08em; text-transform: uppercase; padding: 3px 7px; border-radius: 3px; }
.tdp-tip__along { font-size: 10px; color: var(--tdp-muted); }
.tdp-tip__title { font: 700 16px/1.25 var(--tdp-display); }
.tdp-tip__detail { font-size: 14px; color: var(--tdp-muted); }
.tdp-map__svg { position: absolute; inset: 0; width: 100%; height: 100%; display: block; overflow: visible; }
.tdp-svgtext { font: 600 15px/1 var(--tdp-mono); fill: #1b1d1a; letter-spacing: .08em; }
.tdp-contourtext { font: 600 14px/1 var(--tdp-mono); fill: #8f7f58; letter-spacing: .04em; stroke: #eef0e6; stroke-width: 5px; paint-order: stroke; }
/* The viewBox is 1000 wide, so on a phone 14 units is under 5 px. */
@media (max-width: 640px) { .tdp-contourtext { font-size: 30px; stroke-width: 9px; } }
.tdp-legend {
    position: absolute; left: 12px; bottom: 12px;
    display: flex; flex-wrap: wrap; gap: 6px 14px;
    background: rgba(255, 255, 255, .92); padding: 8px 10px; border-radius: var(--r, 6px);
    font-size: 10px; line-height: 1; color: #1b1d1a;
}
.tdp-legend > span { display: flex; align-items: center; gap: 6px; }
.tdp-legend__start { width: 10px; height: 10px; border-radius: 50%; background: #1b1d1a; }
.tdp-legend__end { width: 9px; height: 9px; border: 2px solid #1b1d1a; background: #fff; }
.tdp-legend__poi { width: 10px; height: 10px; border-radius: 50% 50% 50% 0; transform: rotate(-45deg); background: #1f5f74; }
.tdp-poimark { cursor: pointer; outline: none; }
.tdp-poimark__pin { transition: transform .12s; transform-origin: 0 0; }
.tdp-poimark:hover .tdp-poimark__pin, .tdp-poimark.is-active .tdp-poimark__pin { transform: scale(1.1); }
.tdp-poimark:focus-visible .tdp-poimark__pin { stroke: var(--accent); }
.tdp-tip__sev--poi { background: #1f5f74; color: #fff; }
.tdp-legend__haz { width: 0; height: 0; border-left: 6px solid transparent; border-right: 6px solid transparent; border-bottom: 10px solid #d2452f; }

.tdp-elev { padding: 16px clamp(14px, 2vw, 22px) 14px; border-top: 1px solid var(--tdp-line); }
.tdp-elev--alone { border-top: 0; }
.tdp-elev__head { display: flex; flex-wrap: wrap; justify-content: space-between; gap: 6px 16px; margin-bottom: 12px; }
.tdp-elev__hov { color: var(--tdp-ink); font-size: 11px; }
.tdp-elev__plot { position: relative; padding-left: 56px; }
.tdp-elev__axis { position: absolute; left: 0; font-size: 10px; line-height: 1; color: var(--tdp-meta); letter-spacing: .06em; }
.tdp-elev__axis--max { top: 0; }
.tdp-elev__axis--min { top: 132px; }
.tdp-elev__area { position: relative; height: 150px; cursor: crosshair; touch-action: pan-y; border-bottom: 1px solid var(--tdp-rule); }
.tdp-elev__svg { position: absolute; inset: 0; width: 100%; height: 100%; display: block; }
.tdp-elev__fill { fill: color-mix(in oklch, var(--tdp-route, var(--accent)) 16%, white); }
.tdp-elev__hz { position: absolute; top: 0; bottom: 0; border-left: 2px dashed; pointer-events: none; }
.tdp-elev__hzlabel {
    position: absolute; top: 0; transform: translateX(-50%);
    display: inline-flex; align-items: center;
    padding: 4px 6px; border-radius: 3px; font-size: 9px; line-height: 1; white-space: nowrap; pointer-events: none;
}
.tdp-elev__cross { position: absolute; top: 0; bottom: 0; width: 1px; background: #1b1d1a; pointer-events: none; }
.tdp-elev__dot {
    position: absolute; width: 12px; height: 12px; margin: -6px 0 0 -6px; border-radius: 50%;
    background: var(--tdp-route, var(--accent)); box-shadow: 0 0 0 3px #fff; pointer-events: none;
}
.tdp-elev__ticks { position: relative; height: 22px; }
.tdp-elev__ticks > span { position: absolute; top: 8px; transform: translateX(-50%); font-size: 10px; line-height: 1; color: var(--tdp-meta); white-space: nowrap; }
.tdp-elev__ticks > span:first-child { transform: none; }

.tdp-body { padding-top: 28px; padding-bottom: clamp(48px, 7vw, 96px); }
.tdp-mapcard--joined { border-radius: var(--r, 6px) var(--r, 6px) 0 0; }
.tdp-specs {
    display: grid;
    grid-template-columns: repeat(6, minmax(0, 1fr));
    /* Hairlines drawn by each cell rather than by a gap over a
       colored background, so a trail with fewer figures than columns
       leaves white space, not a grey block. */
    background: #fff;
    border: 1px solid var(--tdp-line);
    border-top: 0;
    border-radius: 0 0 var(--r, 6px) var(--r, 6px);
    overflow: hidden;
}
.tdp-specs--alone { border-top: 1px solid var(--tdp-line); border-radius: var(--r, 6px); margin-top: 8px; }
@media (max-width: 1000px) { .tdp-specs { grid-template-columns: repeat(3, minmax(0, 1fr)); } }
@media (max-width: 560px) { .tdp-specs { grid-template-columns: repeat(2, minmax(0, 1fr)); } }
.tdp-spec { background: #fff; padding: 18px 20px; display: flex; flex-direction: column; gap: 10px; min-width: 0; box-shadow: 1px 0 0 var(--tdp-line), 0 1px 0 var(--tdp-line); }
.tdp-spec__k { color: var(--tdp-meta); line-height: 1; }
.tdp-spec__row { display: flex; align-items: baseline; gap: 5px; white-space: nowrap; min-width: 0; }
.tdp-spec__v { font: 700 clamp(22px, 2vw, 28px)/1 var(--tdp-mono); letter-spacing: -.02em; color: var(--tdp-ink); overflow: hidden; text-overflow: ellipsis; }
.tdp-spec__u { font: 500 13px/1 var(--tdp-mono); color: var(--tdp-meta); }

.tdp-diff { display: inline-flex; align-items: center; gap: 6px; vertical-align: middle; }
.tdp-diff__icon { width: 13px; height: 13px; flex: none; }
.tdp-diff__icon--lg { width: 16px; height: 16px; }
.tdp-details {
    border-top: 1px solid var(--tdp-line);
    padding-top: 28px;
    display: flex;
    flex-direction: column;
    gap: 24px;
    margin-bottom: clamp(32px, 5vw, 56px);
}
.tdp-details__grid { display: grid; grid-template-columns: repeat(3, minmax(0, 1fr)); gap: 24px 32px; }
@media (max-width: 560px) { .tdp-details__grid { grid-template-columns: repeat(2, minmax(0, 1fr)); } }
.tdp-detail { display: flex; flex-direction: column; gap: 8px; min-width: 0; }
.tdp-detail__v { font-size: 18px; line-height: 1.35; color: var(--tdp-ink); text-wrap: pretty; }
.tdp-detail__n { color: var(--tdp-meta); }
.tdp-also { display: flex; flex-wrap: wrap; align-items: center; gap: 10px 12px; }
.tdp-also__k { margin-right: 6px; }
.tdp-feat { display: grid; grid-template-columns: minmax(0, 1fr); gap: 20px; }
.tdp-feat__box { background: #fff; border: 1px solid var(--tdp-line); border-radius: var(--r, 6px); padding: 20px 22px; display: flex; flex-direction: column; gap: 14px; min-width: 0; }
.tdp-feat__list { list-style: none; margin: 0; display: flex; flex-direction: column; }
ul.tdp-feat__box { gap: 0; }
.tdp-feat__box .tdp-feat__list { padding: 0; }
.tdp-feat__row { display: flex; flex-direction: column; gap: 3px; padding: 11px 0; border-top: 1px solid var(--th-line-soft, #eeece6); }
.tdp-feat__row:first-child { border-top: 0; padding-top: 0; }
.tdp-feat__row:last-child { padding-bottom: 0; }
.tdp-feat__row--poi { flex-direction: row; align-items: flex-start; gap: 12px; }
.tdp-feat__glyph { flex: none; }
.tdp-feat__text { flex: 1; min-width: 0; display: flex; flex-direction: column; gap: 3px; }
.tdp-feat__top { display: flex; flex-wrap: wrap; align-items: baseline; gap: 2px 10px; }
.tdp-feat__name { font-weight: 700; font-size: 16px; line-height: 1.3; color: var(--tdp-ink); }
.tdp-feat__type { font-size: 10px; color: var(--tdp-meta); }
.tdp-feat__desc { font-size: 15px; line-height: 1.45; color: var(--tdp-muted); }
.tdp-also__chip { background: #f6f5f1; border: 1px solid var(--tdp-line); border-radius: 999px; padding: 8px 14px; font-size: 15px; line-height: 1; color: var(--tdp-ink); }

.tdp-cols { display: flex; flex-wrap: wrap; gap: clamp(32px, 5vw, 64px); align-items: flex-start; }
.tdp-main { flex: 2 1 460px; min-width: 0; display: flex; flex-direction: column; gap: 36px; }
.tdp-aside { flex: 1 1 280px; min-width: 0; display: flex; flex-direction: column; gap: 16px; }
/* One column on a phone, where the right column would land under the
   whole weather box: hazards are the one thing a visitor must not
   scroll past, so they move to the top. The two columns dissolve into
   one list for this (display: contents) and order lifts the hazards. */
@media (max-width: 860px) {
    .tdp-cols { flex-direction: column; align-items: stretch; gap: 32px; }
    .tdp-main, .tdp-aside { display: contents; }
    .tdp-part--haz { order: -1; }
}
.tdp-part { display: flex; flex-direction: column; gap: 12px; }
/* Built from the trail template's blocks: each part is its own block,
   so the two-column body is the Columns block's job, not this one's. */
.tdp--part .tdp-cols { display: block; }
.tdp--part .tdp-main, .tdp--part .tdp-aside { display: contents; }
.tdp--part .tdp-body { padding-top: 12px; padding-bottom: 24px; }
.tdp--part .tdp-details { margin-bottom: 0; }
.tdp--part-header .tdp-head { padding-bottom: 8px; }
.tdp--part-map { padding-bottom: 12px; }
.tdp--part .tdp-part + .tdp-part { margin-top: 36px; }
.tdp--part .tdp-net, .tdp--part .tdp-more { margin-top: 16px; }
.tdp--part .tdp-aside > * + *, .tdp--part .tdp-main > * + * { margin-top: 28px; }
.tdp-part__head { display: flex; flex-wrap: wrap; justify-content: space-between; align-items: baseline; gap: 8px 16px; }
.tdp-h2 { font: 800 clamp(24px, 2.6vw, 30px)/1.1 var(--tdp-display); font-stretch: 106%; margin: 0; }
.tdp-lede { margin: 0; font-size: 18px; line-height: 1.6; color: #363a35; max-width: 68ch; white-space: pre-line; }
.tdp-more { font-weight: 600; color: var(--tdp-ink); border-bottom: 2px solid var(--accent); text-decoration: none; align-self: flex-start; }
.tdp-more:hover { text-decoration: none; border-bottom-color: var(--tdp-ink); }

.tdp-cond { background: #fff; border: 1px solid var(--tdp-line); border-radius: var(--r, 6px); padding: 20px; display: flex; flex-direction: column; gap: 10px; }
.tdp-cond__top { display: flex; flex-wrap: wrap; align-items: center; gap: 10px; }
.tdp-cond__top .tdp-pill { padding: 4px 9px; }
.tdp-cond__name { font: 700 18px/1.2 var(--tdp-display); }
.tdp-cond__note { margin: 0; font-size: 16px; color: var(--tdp-muted); }

.tdp-haz { border-radius: var(--r, 6px); padding: 16px 18px; display: flex; gap: 14px; align-items: flex-start; }
.tdp-haz__icon { flex: none; margin-top: 1px; }
.tdp-haz__text { flex: 1; min-width: 0; display: flex; flex-direction: column; gap: 6px; }
.tdp-haz__top { display: flex; flex-wrap: wrap; align-items: baseline; gap: 4px 10px; }
.tdp-haz__title { font: 700 17px/1.2 var(--tdp-display); }
.tdp-haz__type { margin-right: 6px; }
.tdp-elev__hzicon { margin-right: 4px; vertical-align: 0; }
.tdp-haz__sev { font-size: 10px; }
.tdp-haz__detail { font-size: 15px; color: #363a35; }
.tdp-haz__by { flex: none; align-self: stretch; display: flex; flex-direction: column; justify-content: center; align-items: flex-end; gap: 6px;
    margin: -16px -18px -16px 0; padding: 16px 18px; border-left: 1px solid rgba(27, 29, 26, .1); font-size: 10px; line-height: 1; color: var(--tdp-meta); text-align: right; white-space: nowrap; }
.tdp-haz__when { color: var(--tdp-ink); font-size: 11px; }
.tdp-nohaz { border: 1px dashed #cfccc2; border-radius: var(--r, 6px); padding: 16px 18px; display: flex; align-items: center; gap: 12px; font-size: 16px; color: var(--tdp-muted); }
.tdp-nohaz__dot { width: 9px; height: 9px; border-radius: 50%; background: #2e9b4f; flex: none; }

.tdp-h3 { font: 800 22px/1.15 var(--tdp-display); margin: 0; }
.tdp-near { display: flex; flex-direction: column; background: #fff; border: 1px solid var(--tdp-line); border-radius: var(--r, 6px); overflow: hidden; }
.tdp-near__row { display: flex; align-items: center; gap: 12px; padding: 14px 16px; border-bottom: 1px solid #eeece6; color: inherit; text-decoration: none; }
.tdp-near__row:last-child { border-bottom: 0; }
.tdp-near__row:hover { background: #faf9f6; text-decoration: none; }
.tdp-near__dot { width: 9px; height: 9px; border-radius: 50%; flex: none; }
.tdp-near__icon { width: 13px; height: 13px; flex: none; }
.tdp-near__text { flex: 1; min-width: 0; display: flex; flex-direction: column; gap: 6px; }
.tdp-near__name { font-weight: 700; font-size: 16px; line-height: 1.2; color: var(--tdp-ink); }
.tdp-near__meta { font-size: 10px; line-height: 1.3; letter-spacing: .08em; color: var(--tdp-meta); }
.tdp-near__go { font-size: 16px; color: var(--tdp-meta); flex: none; }
.tdp-net { background: var(--tint); border-radius: var(--r, 6px); padding: 22px; display: flex; flex-direction: column; gap: 14px; }
.tdp-net__name { font: 800 22px/1.15 var(--tdp-display); }
.tdp-net__link { color: inherit; text-decoration: underline; text-decoration-thickness: 2px; text-underline-offset: 4px; text-decoration-color: color-mix(in srgb, currentColor 30%, transparent); }
.tdp-net__link:hover { text-decoration-color: currentColor; }
.tdp-net__uses { display: flex; flex-wrap: wrap; gap: 6px; }
.tdp-chip { background: #fff; border: 1px solid var(--tdp-rule); border-radius: 999px; padding: 6px 10px; font-size: 10px; line-height: 1; }
.tdp-wx { display: flex; flex-direction: column; gap: 12px; }
.tdp-wx__card { background: #fff; border: 1px solid var(--tdp-line); border-radius: var(--r, 6px); overflow: hidden; display: flex; flex-direction: column; }
.tdp-wx__top { padding: 18px; display: flex; flex-direction: column; gap: 18px; }
.tdp-wx__headrow { display: flex; flex-wrap: wrap; justify-content: space-between; align-items: flex-start; gap: 12px 20px; }
.tdp-wx__now { display: flex; flex-direction: column; gap: 8px; }
.tdp-wx__big { font: 800 clamp(44px, 5vw, 60px)/.9 var(--tdp-display); font-stretch: 106%; letter-spacing: -.02em; color: var(--tdp-ink); }
.tdp-wx__pill { padding: 8px 14px; border-radius: 999px; font-weight: 600; font-size: 15px; line-height: 1; }
.tdp-wx__pill--open { background: #e3f1e6; color: #1d6b35; }
.tdp-wx__pill--caution { background: #fbf0d9; color: #7d5200; }
.tdp-wx__pill--closed { background: #f7e0dc; color: #8f2a1c; }
.tdp-wx__gaugewrap { display: flex; flex-direction: column; gap: 8px; }
.tdp-wx__gauge { position: relative; height: 8px; border-radius: 999px; display: flex; }
.tdp-wx__zone--good { background: #cfe6d5; border-radius: 999px 0 0 999px; }
.tdp-wx__zone--caution { background: #f6e2b4; }
.tdp-wx__zone--closed { flex: 1; background: #f1cbc3; border-radius: 0 999px 999px 0; }
.tdp-wx__knob { position: absolute; top: 50%; width: 18px; height: 18px; margin: -9px 0 0 -9px; border-radius: 50%; background: #1b1d1a; box-shadow: 0 0 0 3px #fff; }
.tdp-wx__scale { position: relative; height: 12px; font: 500 10px/1 var(--tdp-mono); letter-spacing: .08em; color: #7a7e76; text-transform: uppercase; }
.tdp-wx__scale > span { position: absolute; white-space: nowrap; }
.tdp-wx__scale > .is-mid { transform: translateX(-50%); }
.tdp-wx__scale > .is-close { color: #a3321f; }
.tdp-wx__summary { margin: 0; font-size: 16px; line-height: 1.55; color: var(--tdp-muted); text-wrap: pretty; }
.tdp-wx__chartpart { border-top: 1px solid #eeece6; padding: 18px; display: flex; flex-direction: column; gap: 10px; }
.tdp-wx__charthead { display: flex; flex-wrap: wrap; justify-content: space-between; gap: 8px 16px; }
.tdp-wx__legend { display: flex; flex-wrap: wrap; gap: 6px 14px; font: 500 10px/1 var(--tdp-mono); letter-spacing: .08em; color: var(--tdp-meta); text-transform: uppercase; }
.tdp-wx__legend > span { display: flex; align-items: center; gap: 6px; }
.tdp-wx__lg-line { width: 14px; height: 2px; background: #1b1d1a; }
.tdp-wx__lg-dash { width: 14px; border-top: 2px dashed #1b1d1a; }
.tdp-wx__lg-bar { width: 7px; height: 10px; background: #7f9db1; border-radius: 1px; }
.tdp-wx__chart { position: relative; height: 150px; margin-top: 14px; }
.tdp-wx__over { position: absolute; left: 0; right: 0; top: 0; background: #fbeeea; border-radius: 3px 3px 0 0; }
.tdp-wx__thr { position: absolute; left: 0; right: 0; border-top: 1px dashed #c2412d; }
.tdp-wx__thr > span { position: absolute; left: 6px; bottom: 5px; font: 500 10px/1 var(--tdp-mono); letter-spacing: .1em; color: #a3321f; text-transform: uppercase; }
.tdp-wx__nowline { position: absolute; top: -10px; bottom: 0; border-left: 1px solid #1b1d1a; }
.tdp-wx__nowline > span { position: absolute; top: -6px; left: 0; transform: translateX(-50%); background: #1b1d1a; color: #fff; font: 600 9px/1 var(--tdp-mono); letter-spacing: .1em; padding: 3px 5px; border-radius: 2px; text-transform: uppercase; }
.tdp-wx__svg { position: absolute; inset: 0; width: 100%; height: 100%; overflow: visible; }
.tdp-wx__area { fill: var(--accent); fill-opacity: .1; }
.tdp-wx__line { fill: none; stroke: #1b1d1a; stroke-width: 2.5; vector-effect: non-scaling-stroke; stroke-linejoin: round; stroke-linecap: round; }
.tdp-wx__line--ahead { stroke-width: 2; stroke-dasharray: 5 4; }
.tdp-wx__today { position: absolute; width: 14px; height: 14px; margin: 0 0 -7px -7px; border-radius: 50%; background: var(--accent); box-shadow: 0 0 0 3px #fff; }
.tdp-wx__bars { display: grid; grid-template-columns: repeat(8, minmax(0, 1fr)); height: 40px; align-items: end; border-top: 1px solid #cfccc2; padding-top: 4px; }
.tdp-wx__barcol { height: 100%; display: flex; flex-direction: column; align-items: center; justify-content: flex-end; gap: 3px; }
.tdp-wx__barcol > span { font: 500 9px/1 var(--tdp-mono); color: var(--tdp-meta); }
.tdp-wx__bar { width: 36%; max-width: 18px; max-height: 26px; background: #7f9db1; border-radius: 2px 2px 0 0; }
.tdp-wx__bar--ahead { background: repeating-linear-gradient(135deg, #b9cad6 0 3px, #dfe7ec 3px 6px); }
.tdp-wx__days { display: grid; grid-template-columns: repeat(8, minmax(0, 1fr)); text-align: center; }
.tdp-wx__day { display: flex; flex-direction: column; gap: 5px; min-width: 0; padding-top: 6px; border-top: 1px solid #eeece6; }
.tdp-wx__dname { font: 600 10px/1 var(--tdp-mono); letter-spacing: .04em; color: var(--tdp-meta); }
.tdp-wx__day.is-today .tdp-wx__dname { color: #1b1d1a; }
.tdp-wx__dval { font: 600 12px/1 var(--tdp-mono); color: #1b1d1a; }
.tdp-wx__dval.is-over { color: #a3321f; }
/* Four readings: one row of four, or two by two on a phone — never
   three and one, which left a grey hole beside the last. */
.tdp-wx__readings { border-top: 1px solid #eeece6; display: grid; grid-template-columns: repeat(4, minmax(0, 1fr)); background: #fff; overflow: hidden; }
@media (max-width: 560px) { .tdp-wx__readings { grid-template-columns: repeat(2, minmax(0, 1fr)); } }
.tdp-wx__reading { background: #fff; padding: 14px 18px; display: flex; flex-direction: column; gap: 8px; min-width: 0; box-shadow: 1px 0 0 #eeece6, 0 1px 0 #eeece6; }
.tdp-wx__rv { font: 600 18px/1 var(--tdp-mono); color: #1b1d1a; }
.tdp-wx__foot { border-top: 1px solid #eeece6; padding: 12px 18px; background: #faf9f6; font-size: 10px; line-height: 1.4; color: #7a7e76; }
</style>

<style>
/* Unscoped: these reach from a Columns block (either template) to the
   trail parts inside it. A Columns block holding trail parts lines up
   with the parts above it rather than with the wider page grid, and
   its cells drop the card padding a column gives other blocks, since
   together they are one page. */
.tiq-cols .tdp-wrap, .th-cols .tdp-wrap { max-width: none; padding-left: 0; padding-right: 0; }
.tiq-cols:has(.tdp--part) { padding-left: 0; padding-right: 0; padding-top: 8px; }
.tiq-cols:has(.tdp--part) .tiq-cols-grid {
    max-width: 1240px; box-sizing: border-box;
    padding-left: clamp(16px, 4vw, 40px); padding-right: clamp(16px, 4vw, 40px);
}
.th-cols:has(.tdp--part) { padding-top: 8px; }
.tiq-cols-col > .tdp--part { padding: 0 !important; border-radius: 0; }
</style>
